import { trigger, state, style, transition, animate } from '@angular/animations';
import { Component, inject, input, output, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  faCloudArrowUp,
  faGear,
  faTrashCan,
  faInfoCircle,
  faArrowUpRightFromSquare,
  faLock,
  faChevronDown,
  faChevronUp,
  faPlus,
  faClose,
  faEllipsisVertical
} from '@fortawesome/pro-regular-svg-icons';
import { CustomTerm, ProfanityFilterOptions } from '@empathy/survey/base';

import { MainTabs } from '../../../models/main-tabs.enum';
import { ICustomTerm } from '../../../models/survey-editor/custom-terms.interface';
import { SurveyEditorService } from '../../../services/survey-editor.service';
import { ImportCsvDialogComponent } from '../import-csv-dialog/import-csv-dialog.component';

@Component({
  selector: 'empathy-terms-form',
  templateUrl: 'terms-form.component.html',
  styleUrls: ['./terms-form.component.scss'],
  animations: [
    trigger('toggleContent', [
      state(
        'hidden',
        style({
          height: '0px',
          opacity: 0,
          overflow: 'hidden'
        })
      ),
      state(
        'visible',
        style({
          height: '*',
          opacity: 1,
          overflow: 'hidden'
        })
      ),
      transition('hidden <=> visible', [animate('250ms cubic-bezier(.17,.67,1,1.04)')])
    ])
  ]
})
export class TermsFormComponent {
  monitorProblematicIndCtrl = input.required<FormControl<boolean>>();
  flagCurseWordsIndCtrl = input.required<FormControl<boolean>>();
  flagGibberishIndCtrl = input.required<FormControl<boolean>>();
  customErrorMessageIndCtrl = input.required<FormControl<boolean>>();
  customErrorMessageCtrl = input.required<FormControl<string>>();
  blockedTermsCtrl = input.required<FormArray<FormGroup<ICustomTerm>>>();
  flaggedTermsCtrl = input.required<FormArray<FormGroup<ICustomTerm>>>();
  questionLevel = input<boolean>();
  allowToggleSettings = input<boolean>(true);
  gibberishSupported = input<boolean>(true);

  csvBlocksImport = output<CustomTerm[]>();
  csvFlagsImport = output<CustomTerm[]>();

  selectedBlockTerms$ = signal<number[]>([]);
  selectedFlagTerms$ = signal<number[]>([]);
  showBlockTerms$ = signal<boolean>(true);
  showFlagTerms$ = signal<boolean>(true);

  readonly tooltip = `Choose how to handle responses containing profanity:\n
      Disabled - No action taken.\n
      Flag - Mark responses for review but still collect them.\n
      Block - Prevent the response from being submitted.\n
    `;

  faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faCloudArrowUp = faCloudArrowUp;
  faClose = faClose;
  faEllipsisVertical = faEllipsisVertical;
  faInfoCircle = faInfoCircle;
  faGear = faGear;
  faLock = faLock;
  faPlus = faPlus;
  faTrashCan = faTrashCan;

  profanityFilterOptions = ProfanityFilterOptions;

  private dialog = inject(MatDialog);
  private surveyEditorService = inject(SurveyEditorService);

  onAddNewBlockTerm(): void {
    this.blockedTermsCtrl().push(
      new FormGroup<ICustomTerm>({
        type: new FormControl<'contains' | 'exact'>('contains'),
        term: new FormControl('')
      })
    );
  }

  onAddNewFlagTerm(): void {
    this.flaggedTermsCtrl().push(
      new FormGroup<ICustomTerm>({
        type: new FormControl<'contains' | 'exact'>('contains'),
        term: new FormControl('')
      })
    );
  }

  onDeleteBlockTerm(index: number): void {
    this.blockedTermsCtrl().removeAt(index);
  }

  onDeleteFlagTerm(index: number): void {
    this.flaggedTermsCtrl().removeAt(index);
  }

  onSelectAllBlockTerms(checked: boolean): void {
    const controls = this.blockedTermsCtrl().controls;
    if (this.selectedBlockTerms$().length === controls.length && !checked) {
      this.selectedBlockTerms$.set([]);
    } else {
      for (let i = 0; i < controls.length; i++) {
        if (!this.selectedBlockTerms$().includes(i)) {
          this.selectedBlockTerms$.update((selected) => [...selected, i]);
        }
      }
    }
  }

  onSelectAllFlagTerms(checked: boolean): void {
    const controls = this.flaggedTermsCtrl().controls;
    if (this.selectedFlagTerms$().length === controls.length && !checked) {
      this.selectedFlagTerms$.set([]);
    } else {
      for (let i = 0; i < controls.length; i++) {
        if (!this.selectedFlagTerms$().includes(i)) {
          this.selectedFlagTerms$.update((selected) => [...selected, i]);
        }
      }
    }
  }

  onSelectBlockTerm(index: number, checked: boolean): void {
    if (this.selectedBlockTerms$().includes(index) && !checked) {
      this.selectedBlockTerms$.update((selected) => selected.filter((i) => i !== index));
    } else if (!this.selectedBlockTerms$().includes(index) && checked) {
      this.selectedBlockTerms$.update((selected) => [...selected, index]);
    }
  }

  onSelectFlagTerm(index: number, checked: boolean): void {
    if (this.selectedFlagTerms$().includes(index) && !checked) {
      this.selectedFlagTerms$.update((selected) => selected.filter((i) => i !== index));
    } else if (!this.selectedFlagTerms$().includes(index) && checked) {
      this.selectedFlagTerms$.update((selected) => [...selected, index]);
    }
  }

  onSetAllSelectedBlocksToContains(): void {
    for (const index of this.selectedBlockTerms$()) {
      this.blockedTermsCtrl().at(index).get('type').setValue('contains');
    }
  }

  onSetAllSelectedFlagsToContains(): void {
    for (const index of this.selectedFlagTerms$()) {
      this.flaggedTermsCtrl().at(index).get('type').setValue('contains');
    }
  }

  onSetAllSelectedBlocksToExact(): void {
    for (const index of this.selectedBlockTerms$()) {
      this.blockedTermsCtrl().at(index).get('type').setValue('exact');
    }
  }

  onSetAllSelectedFlagsToExact(): void {
    for (const index of this.selectedFlagTerms$()) {
      this.flaggedTermsCtrl().at(index).get('type').setValue('exact');
    }
  }

  onDeleteAllSelectedBlocks(): void {
    const selectedIndexes = this.selectedBlockTerms$().sort((a, b) => b - a);

    for (const index of selectedIndexes) {
      this.blockedTermsCtrl().removeAt(index);
    }

    this.blockedTermsCtrl().push(
      new FormGroup<ICustomTerm>({
        type: new FormControl<'contains' | 'exact'>('contains'),
        term: new FormControl('')
      })
    );

    this.selectedBlockTerms$.set([]);
  }

  onDeleteAllSelectedFlags(): void {
    const selectedIndexes = this.selectedFlagTerms$().sort((a, b) => b - a);

    for (const index of selectedIndexes) {
      this.flaggedTermsCtrl().removeAt(index);
    }

    this.flaggedTermsCtrl().push(
      new FormGroup<ICustomTerm>({
        type: new FormControl<'contains' | 'exact'>('contains'),
        term: new FormControl('')
      })
    );

    this.selectedFlagTerms$.set([]);
  }

  onOpenBlocksImportCsvDialog(): void {
    this.dialog
      .open(ImportCsvDialogComponent, {})
      .afterClosed()
      .subscribe((csvContent: CustomTerm[]) => {
        this.csvBlocksImport.emit(csvContent);
      });
  }

  onOpenFlagsImportCsvDialog(): void {
    this.dialog
      .open(ImportCsvDialogComponent, {})
      .afterClosed()
      .subscribe((csvContent: CustomTerm[]) => {
        this.csvFlagsImport.emit(csvContent);
      });
  }

  onBlocksPasteTerms(event: string[], pastedOnIndex: number): void {
    event.forEach((text, index) => {
      const newIndex = pastedOnIndex + index;
      const newTerm = new FormGroup<ICustomTerm>({
        type: new FormControl<'contains' | 'exact'>('contains'),
        term: new FormControl(text)
      });

      if (newIndex >= this.blockedTermsCtrl().controls.length) {
        this.blockedTermsCtrl().push(newTerm);
      } else {
        if (!this.blockedTermsCtrl().at(newIndex)?.get('term').value) {
          this.blockedTermsCtrl().at(newIndex).get('term').setValue(text);
        } else {
          this.blockedTermsCtrl().push(newTerm);
        }
      }
    });
  }

  onFlagsPasteTerms(event: string[], pastedOnIndex: number): void {
    event.forEach((text, index) => {
      const newIndex = pastedOnIndex + index;
      const newTerm = new FormGroup<ICustomTerm>({
        type: new FormControl<'contains' | 'exact'>('contains'),
        term: new FormControl(text)
      });

      if (newIndex >= this.flaggedTermsCtrl().controls.length) {
        this.flaggedTermsCtrl().push(newTerm);
      } else {
        if (!this.flaggedTermsCtrl().at(newIndex)?.get('term').value) {
          this.flaggedTermsCtrl().at(newIndex).get('term').setValue(text);
        } else {
          this.flaggedTermsCtrl().push(newTerm);
        }
      }
    });
  }

  openSettingsTab(): void {
    this.surveyEditorService.mainTab$.set(MainTabs.Settings);
  }
}
